export type EventCategory = "Música" | "Cultura" | "Gastronomía" | "Deportes" | "Familia" | "Niños" | "Ferias";

// Mismos dominios que `images.remotePatterns` en next.config.ts — si el host
// no calza con ninguno, Next.js Image lanza error al intentar optimizarlo,
// así que esos casos deben pasar `unoptimized`. Local paths siempre son
// optimizables (Next los sirve desde el propio dominio).
const OPTIMIZABLE_HOST_PATTERNS = [/\.cl$/i, /\.tchile\.com$/i, /\.wp\.com$/i, /\.wordpress\.com$/i, /^res\.cloudinary\.com$/i];

export function isOptimizableImageHost(src: string): boolean {
  if (!src.startsWith("http")) return true;
  try {
    const { hostname } = new URL(src);
    return OPTIMIZABLE_HOST_PATTERNS.some((re) => re.test(hostname));
  } catch {
    return false;
  }
}

// Usado para las URLs long-tail de SEO (/eventos/comuna/[distrito]/[categoria]).
// No es reversible por sí solo (por eso las páginas hacen match contra la
// lista real de comunas/categorías, no intentan reconstruir el nombre original).
export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");
}

export type EventStatus = "draft" | "pending" | "approved" | "rejected" | "canceled";

// Único lugar donde se definen las clases/etiquetas de status — antes estaban
// copiadas dos veces (org-events-table.tsx y mis-eventos/page.tsx), cada
// copia con su propio union type redeclarado en vez de reusar EventStatus.
export const EVENT_STATUS_STYLES: Record<EventStatus, string> = {
  draft: "bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400",
  pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
  approved: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  canceled: "bg-stone-100 text-stone-500 dark:bg-stone-900/40 dark:text-stone-400",
};

// Los nombres de cada estado (por idioma) están en events.i18n.ts → statuses.

export interface CiudadVivaEvent {
  slug: string;
  // Código ISO del país del evento (CL, US…).
  country: string;
  title: string;
  image: string;
  category: EventCategory;
  date: string;
  dateISO: string;
  time: string;
  schedule?: string;
  dates?: string[];
  // Campos crudos (sin formatear) para "agregar al calendario" — dateISO ya
  // es "la próxima fecha relevante para mostrar en la card", no
  // necesariamente el date.start real cuando hay dates[].
  dateStartISO?: string;
  dateEndISO?: string;
  timeStart?: string;
  timeEnd?: string;
  recurrenceType?: "none" | "weekly" | "custom";
  region: string;
  district: string;
  venue: string;
  // Evento online (sin lugar físico).
  isOnline?: boolean;
  price: number | "free" | "consultar";
  organizer: string;
  organizerSlug?: string;
  organizerLogoUrl?: string;
  organizerWebsite?: string;
  organizerLinks?: Array<{ name: string; url: string }>;
  organizerTrusted?: boolean;
  // "Difusión": organizador real del evento, cuando es distinto de quien lo
  // publica (los campos organizer* de arriba). Puramente atribución.
  realOrganizerName?: string;
  realOrganizerOrgSlug?: string;
  realOrganizerOrgLogoUrl?: string;
  realOrganizerOrgTrusted?: boolean;
  description: string;
  tags: string[];
  location?: { lat: number; lng: number };
  today?: boolean;
  weekend?: boolean;
  sponsored?: boolean;
  ctaLabel: string;
  ctaUrl?: string;
  status?: EventStatus;
  cancellationReason?: string;
  canceledAt?: string;
  active?: boolean;
  timezone?: string;
  interestCount?: number;
}

export const categories: { name: EventCategory; emoji: string }[] = [
  { name: "Música",      emoji: "🎵" },
  { name: "Cultura",     emoji: "🎭" },
  { name: "Familia",     emoji: "👨‍👩‍👧" },
  { name: "Deportes",    emoji: "🏅" },
  { name: "Niños",       emoji: "🎈" },
  { name: "Ferias",      emoji: "🎪" },
  { name: "Gastronomía", emoji: "🍽️" },
];

// Eventos de las grillas "Hoy" y "Gratis" de la home (servidor y navegador).
export function pickHomeEvents(events: CiudadVivaEvent[], kind: "today" | "free"): CiudadVivaEvent[] {
  if (kind === "free") return events.filter((e) => e.price === "free").slice(0, 6);
  const today = events.filter((e) => e.today);
  return today.length ? today : events.slice(0, 3);
}
